import { useEffect, useRef, useState } from 'react';
import { RotateCcw, Pause, Play, Plus, Minus } from 'lucide-react';
import { DEMO_EVENTS, arcPoints } from './globeData';
import { loadCesium } from './loadCesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

export const GlobeScene = ({ mode, events = [], onSelect, onPhase }) => {
  const host = useRef(null);
  const api = useRef(null);
  const handlers = useRef({ onSelect, onPhase });
  handlers.current = { onSelect, onPhase };
  const [ready, setReady] = useState(false);
  const [imageryReady, setImageryReady] = useState(false);
  const [error, setError] = useState('');
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let viewer;
    let picker;
    let observer;
    (async () => {
      const C = await loadCesium();
      if (cancelled) return;
      const imagery = await C.TileMapServiceImageryProvider.fromUrl(C.buildModuleUrl('Assets/Textures/NaturalEarthII'));
      if (cancelled) return;
      viewer = new C.Viewer(host.current, {
        baseLayer: new C.ImageryLayer(imagery),
        baseLayerPicker: false, geocoder: false, homeButton: false, sceneModePicker: false,
        navigationHelpButton: false, animation: false, timeline: false, fullscreenButton: false,
        selectionIndicator: false, infoBox: false, shouldAnimate: true,
        terrainProvider: new C.EllipsoidTerrainProvider(),
        contextOptions: { webgl: { preserveDrawingBuffer: true } },
      });
      viewer.scene.backgroundColor = C.Color.fromCssColorString('#05080e');
      viewer.scene.skyBox.show = false;
      viewer.imageryLayers.get(0).brightness = 0.72;
      viewer.imageryLayers.get(0).contrast = 1.15;
      viewer.scene.globe.enableLighting = false;
      viewer.scene.globe.baseColor = C.Color.fromCssColorString('#142d3c');
      viewer.scene.skyAtmosphere.show = true;
      viewer.scene.screenSpaceCameraController.minimumZoomDistance = 8000000;
      viewer.scene.screenSpaceCameraController.maximumZoomDistance = 75000000;
      viewer.resolutionScale = Math.min(window.devicePixelRatio, 1.5);
      const home = () => {
        viewer.resize();
        const radius = C.Ellipsoid.WGS84.maximumRadius;
        const fov = Math.min(viewer.camera.frustum.fovy, viewer.camera.frustum.fov);
        const altitude = radius / Math.sin(fov / 2) * 1.2 - radius;
        viewer.camera.setView({ destination: C.Cartesian3.fromDegrees(35, 22, altitude) });
      };
      home();
      observer = new ResizeObserver(() => { if (!viewer.isDestroyed()) home(); });
      observer.observe(host.current);
      const removeReadyListener = viewer.scene.postRender.addEventListener(() => {
        if (viewer.scene.globe.tilesLoaded) {
          setImageryReady(true);
          host.current.dataset.imagery = 'ready';
          removeReadyListener();
        }
      });
      imagery.errorEvent.addEventListener(() => setError('Earth imagery could not load. Event details remain available.'));
      picker = new C.ScreenSpaceEventHandler(viewer.scene.canvas);
      picker.setInputAction(click => {
        const picked = viewer.scene.pick(click.position);
        if (picked?.id?.eventData) handlers.current.onSelect?.(picked.id.eventData);
      }, C.ScreenSpaceEventType.LEFT_CLICK);
      const start = C.JulianDate.clone(viewer.clock.currentTime);
      let lastPhase = -1;
      viewer.clock.onTick.addEventListener(clock => {
        const seconds = C.JulianDate.secondsDifference(clock.currentTime, start);
        const phase = Math.floor((seconds % 12) / 4);
        if (phase !== lastPhase) { lastPhase = phase; handlers.current.onPhase?.(phase); }
      });
      viewer.scene.renderError.addEventListener(() => setError('3D rendering stopped. Your event list remains available.'));
      api.current = { viewer, C, home, start };
      // Expose only non-sensitive rendering diagnostics for browser verification.
      host.current.dataset.engine = 'cesium';
      setReady(true);
    })().catch(() => { if (!cancelled) setError('3D is unavailable on this device. Event details remain available below.'); });
    return () => {
      cancelled = true;
      api.current = null;
      picker?.destroy();
      observer?.disconnect();
      if (viewer && !viewer.isDestroyed()) viewer.destroy();
    };
  }, []);

  useEffect(() => {
    if (!ready || !api.current) return;
    const { C, viewer, start } = api.current;
    viewer.entities.removeAll();
    const cyan = C.Color.fromCssColorString('#4ae3e0');
    const red = C.Color.fromCssColorString('#fb657e');
    const green = C.Color.fromCssColorString('#66eba7');
    const phase = time => Math.floor((C.JulianDate.secondsDifference(time, start) % 12) / 4);
    if (mode === 'demo') {
      DEMO_EVENTS.forEach((event, index) => {
        const points = arcPoints(C, event.from, event.to);
        const line = viewer.entities.add({ polyline: { positions: points, width: 2, material: new C.PolylineGlowMaterialProperty({ glowPower: 0.15, color: new C.CallbackProperty(time => phase(time) === 2 ? green : red, false) }) } });
        line.eventData = event;
        const node = viewer.entities.add({
          position: C.Cartesian3.fromDegrees(...event.to, 35000),
          point: { pixelSize: 11, color: cyan, outlineColor: cyan.withAlpha(0.2), outlineWidth: 7 },
          label: { text: event.location.toUpperCase(), show: index !== 2, font: '11px monospace', fillColor: cyan, pixelOffset: new C.Cartesian2(0, 22), showBackground: true, backgroundColor: C.Color.BLACK.withAlpha(0.65), distanceDisplayCondition: new C.DistanceDisplayCondition(0, 55000000) },
        });
        node.eventData = event;
        viewer.entities.add({ position: C.Cartesian3.fromDegrees(...event.from, 25000), point: { pixelSize: 5, color: red } });
        viewer.entities.add({
          position: new C.CallbackProperty(time => points[Math.min(60, Math.floor(((C.JulianDate.secondsDifference(time, start) + index * 0.5) % 4) / 4 * 61))], false),
          point: { pixelSize: 7, color: new C.CallbackProperty(time => phase(time) === 2 ? green : red, false) },
        });
        viewer.entities.add({
          position: new C.CallbackProperty(time => points[60 - Math.min(60, Math.floor((C.JulianDate.secondsDifference(time, start) % 4) / 4 * 61))], false),
          point: { pixelSize: 9, color: cyan, show: new C.CallbackProperty(time => phase(time) === 1, false) },
        });
      });
    } else {
      events.filter(e => e.location).forEach(event => {
        const node = viewer.entities.add({ position: C.Cartesian3.fromDegrees(event.location.longitude, event.location.latitude, 35000),
          point: { pixelSize: 12, color: event.outcome === 'flagged' ? red : cyan, outlineWidth: 5, outlineColor: cyan.withAlpha(0.2) },
        });
        node.eventData = event;
      });
    }
  }, [ready, mode, events]);

  const toggle = () => {
    if (!api.current) return;
    api.current.viewer.clock.shouldAnimate = paused;
    setPaused(!paused);
  };
  return <div className="globe-scene" data-testid="globe-scene">
    <div ref={host} className="globe-canvas" data-testid="cesium-container" aria-busy={!imageryReady} aria-label="Interactive Earth. Drag to rotate; scroll to zoom." />
    {(!ready || !imageryReady) && !error && <div className="globe-fallback" data-testid="globe-loading">Initializing Earth…</div>}
    {error && <div className="globe-fallback" role="alert" data-testid="globe-error">{error}</div>}
    <div className="globe-controls">
      <button data-testid="globe-reset" title="Reset view" aria-label="Reset view" onClick={() => api.current?.home()}><RotateCcw size={16}/></button>
      <button data-testid="globe-zoom-in" title="Zoom in" aria-label="Zoom in" onClick={() => api.current?.viewer.camera.zoomIn(2000000)}><Plus size={16}/></button>
      <button data-testid="globe-zoom-out" title="Zoom out" aria-label="Zoom out" onClick={() => api.current?.viewer.camera.zoomOut(2000000)}><Minus size={16}/></button>
      <button data-testid="globe-pause" title={paused ? 'Resume animation' : 'Pause animation'} aria-label={paused ? 'Resume animation' : 'Pause animation'} aria-pressed={paused} onClick={toggle}>{paused ? <Play size={16}/> : <Pause size={16}/>}</button>
    </div>
  </div>;
};